import {useState} from 'react';

import {useThemeColors} from '@nfq/colors';
import {HTTP_METHODS} from '@nfq/typed-next-api';

import {useSendApplicationForm} from 'Application/useCases/useSendApplicationForm';

import type {ApplicationFormData} from 'Domain/entities/ApplicationForm';
import type {HandleFormSubmit} from 'Shared/formHelperTypes';

/**
 * Custom hook for managing the contact block form functionality and state.
 * This hook provides form submission handling, loading states, and success states for the contact form component.
 * It integrates with the contact form use case and provides theme colors for styling purposes.
 *
 * @param id The unique identifier of the application block instance.
 * @returns An object containing the contact block functionality and state.
 *
 * @example
 * ```tsx
 * const ContactBlock = () => {
 *     const {colors, handleSubmit, isLoading, isSend} = useContactBlock();
 *
 *     return (
 *         <form onSubmit={handleSubmit}>
 *             <input name="email" type="email" />
 *             <button type="submit" disabled={isLoading}>
 *                 {isLoading ? 'Sending...' : 'Send'}
 *             </button>
 *             {isSend && <p>Message sent successfully!</p>}
 *         </form>
 *     );
 * };
 * ```
 */
export const useApplicationBlock = (id: string) => {
    const colors = useThemeColors();
    const [isLoading, setIsLoading] = useState(false);
    const [isSend, setIsSend] = useState(false);
    const {error, sendApplicationForm} = useSendApplicationForm(id);

    /**
     * Handles the submission of the contact form data to the backend service.
     * This function manages the loading state during submission and handles both successful and error scenarios.
     * It sets the success state when the form is successfully submitted and resets loading state appropriately.
     *
     * @param props        The form submission props.
     * @param props.values The contact form data containing user input fields.
     * @returns A promise that resolves when the form submission is complete.
     *
     * @example
     * ```tsx
     * const formData = {
     *     name: 'John Doe',
     *     email: 'john@example.com',
     *     message: 'Hello world'
     * };
     *
     * await handleSubmit({values: formData});
     * ```
     */
    const handleSubmit: HandleFormSubmit<ApplicationFormData> = async ({values}) => {
        setIsLoading(true);
        try {
            await sendApplicationForm({
                body: {
                    ...values,
                    jobId: id
                },
                method: HTTP_METHODS.POST
            });
        } catch (err) {
            setIsLoading(false);
            setIsSend(false);

            return;
        }
        setIsSend(true);
        setIsLoading(false);
    };

    return {
        colors,
        error,
        handleSubmit,
        isLoading,
        isSend
    };
};